import React, { useMemo, useState } from 'react';
import type { UserType } from '../../graphql/generated/graphql';
import type { EntitySelectConfig } from '../../search/entitySearch/EntitySelect';
import EntitySelect from '../../search/entitySearch/EntitySelect';
import useAllUsers from '../../user/useAllUsers';
import styles from './TopicUserSelect.module.scss';
import type { FullGroupData } from '../../search/entitySearch/useEntitySearch';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const TOPIC_FRAGMENT = gql(`
  fragment TopicUserSelect_topic on Topic {
    id
    name
    parent {
      id
    }
    membership {
      ownerUserIds
      memberUserIds
      ownerGroupIds
      memberGroupIds
    }
  }
`);

interface TopicUserSelectProps {
  topic: DocumentType<typeof TOPIC_FRAGMENT>;
  inheritable?: boolean;
  onSelectUser: (user: { id: string; type: UserType }) => unknown;
  onSelectGroup: (group: FullGroupData) => unknown;
  onInherit: () => unknown;
}

const TopicUserSelect = ({
  topic,
  inheritable = false,
  onSelectUser,
  onSelectGroup,
  onInherit,
}: TopicUserSelectProps) => {
  const [inputValue, setInputValue] = useState('');
  const allUsers = useAllUsers();

  const config = useMemo<EntitySelectConfig>(() => {
    const {
      membership: {
        ownerUserIds,
        memberUserIds,
        ownerGroupIds,
        memberGroupIds,
      },
      parent,
    } = topic;
    const userIdSet = new Set([
      ...(memberUserIds || []),
      ...(ownerUserIds || []),
    ]);
    const groupIdSet = new Set([
      ...(memberGroupIds || []),
      ...(ownerGroupIds || []),
    ]);

    const spaces: EntitySelectConfig = {
      group: { filter: (group) => !groupIdSet.has(group.id) },
      user: {
        filter: (user) => !userIdSet.has(user.id),
      },
    };

    if (inheritable && parent) {
      return {
        topic: { filter: (t) => t.id === parent.id },
        ...spaces,
      };
    }

    return spaces;
  }, [inheritable, topic]);

  if (allUsers.length === 0) {
    return null;
  }

  const placeholder = `Add member to ${topic.name}`;

  return (
    <>
      {!inputValue && (
        <div className={styles.helpText}>
          Members will receive notifications for new posts, and can manage other
          members in this topic.
        </div>
      )}
      <EntitySelect
        className={styles.select}
        disableMenuPortal
        inputValue={inputValue}
        noOptionsMessage={() => 'No users or groups by that name were found.'}
        onInputChange={(value: string) => setInputValue(value)}
        onChange={(entity) => {
          setInputValue('');

          if (!entity) return;
          if (entity.type === 'user') onSelectUser(entity.user);
          if (entity.type === 'group') onSelectGroup(entity.group);
          if (entity.type === 'topic') onInherit();
        }}
        placeholder={placeholder}
        value={null}
        config={config}
        aria-label={placeholder}
      />
    </>
  );
};

TopicUserSelect.fragments = {
  topic: TOPIC_FRAGMENT,
};

export default TopicUserSelect;
